/**
 * Sets a CSS custom property on an element to its largest side (max of width/height),
 * keeps it updated on resize, and returns a handle with .destroy().
 *
 * @param {Element} el - The element to observe.
 * @param {string} propName - CSS custom property name to set (default: --largest-side).
 * @returns {{ destroy: () => void }}
 */
export function bindLargestSideVar(el, propName = '--largest-side') {
  if (!(el instanceof Element)) {
    throw new TypeError('Expected a DOM Element');
  }

  const set = (n) => el.style.setProperty(propName, `${Math.round(n)}px`);

  const updateFromEntry = (entry) => {
    let w, h;

    // Prefer border-box (includes padding+border) if available
    if (entry.borderBoxSize) {
      const box = Array.isArray(entry.borderBoxSize)
        ? entry.borderBoxSize[0]
        : entry.borderBoxSize;
      if (box && 'inlineSize' in box && 'blockSize' in box) {
        w = box.inlineSize;
        h = box.blockSize;
      }
    }

    // Fallbacks
    if (w == null || h == null) {
      if (entry.contentRect) {
        w = entry.contentRect.width;
        h = entry.contentRect.height;
      } else {
        const r = entry.target.getBoundingClientRect();
        w = r.width;
        h = r.height;
      }
    }

    set(Math.max(w, h));
  };

  const updateImmediate = () => {
    const r = el.getBoundingClientRect();
    set(Math.max(r.width, r.height));
  };

  // Initial set
  updateImmediate();

  // Observe with ResizeObserver if available
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) updateFromEntry(entry);
    });
    try {
      ro.observe(el, { box: 'border-box' });
    } catch {
      // Some browsers may not support {box}; fall back
      ro.observe(el);
    }

    return {
      destroy() {
        try {
          ro.unobserve(el);
        } catch {}
        ro.disconnect();
        // Optional: remove the property line below if you want to keep the last value
        // el.style.removeProperty(propName);
      },
    };
  }

  // Fallback when ResizeObserver isn't supported
  const onWinResize = () => updateImmediate();
  window.addEventListener('resize', onWinResize, { passive: true });

  return {
    destroy() {
      window.removeEventListener('resize', onWinResize);
      // Optional: remove the property
      // el.style.removeProperty(propName);
    },
  };
}

// Example:
// const handle = bindLargestSideVar(document.querySelector('.box'), '--max-side');
// handle.destroy();
